import type { IDataSourceItem } from "./types";

interface ConnectorFlowProps {
    items: IDataSourceItem[];
    cardWidth?: number;
    gap?: number;
}

const PATH_LENGTH = 1000;
const DURATION = 0.6;
const DELAY = 0.08;

/**
 * Renders SVG connector lines between data source cards and a central hub.
 * Left items flow INTO the hub (arrow pointing down), right items flow OUT (arrow pointing up).
 */
export const ConnectorFlow = ({ items, cardWidth = 113, gap = 24 }: ConnectorFlowProps) => {
    // Find hub position and count items on each side
    const hubIndex = items.findIndex((item) => item.isHub);
    const leftItemsCount = items.filter((item, index) => !item.isHub && index < hubIndex).length;
    const rightItemsCount = items.filter((item, index) => !item.isHub && index > hubIndex).length;

    // SVG path geometry constants
    const verticalDrop = 8;
    const curveRadius = 8;
    const lineY = verticalDrop + curveRadius;
    const arrowSize = 10;
    const hubSpacing = 15;

    // Calculate X positions for layout
    const totalLeftWidth = leftItemsCount * cardWidth + (leftItemsCount - 1) * gap;
    const hubStartX = totalLeftWidth + gap;
    const hubCenterX = hubStartX + cardWidth / 2;
    const rightStartX = hubStartX + cardWidth + gap;
    const totalWidth = rightStartX + rightItemsCount * cardWidth + (rightItemsCount - 1) * gap;

    // Get non-hub items split by side
    const leftItems = items.filter((item, index) => !item.isHub && index < hubIndex);
    const rightItems = items.filter((item, index) => !item.isHub && index > hubIndex);

    // Generate SVG paths for left-side connectors (flow toward hub)
    const leftPaths = leftItems.map((item, i) => {
        const cardCenterX = i * (cardWidth + gap) + cardWidth / 2;
        const endX = hubCenterX - hubSpacing;
        return {
            id: `left-${item.id}`,
            d: `M${cardCenterX} 0 L${cardCenterX} ${verticalDrop} Q${cardCenterX} ${lineY} ${cardCenterX + curveRadius} ${lineY} L${
                endX - curveRadius
            } ${lineY} Q${endX} ${lineY} ${endX} ${lineY - curveRadius} L${endX} ${arrowSize}`,
            disabled: item.disabled,
            delay: i * DELAY,
        };
    });

    // Generate SVG paths for right-side connectors (flow away from hub - path starts at hub)
    const rightPaths = rightItems.map((item, i) => {
        const cardCenterX = rightStartX + i * (cardWidth + gap) + cardWidth / 2;
        const startX = hubCenterX + hubSpacing;
        return {
            id: `right-${item.id}`,
            d: `M${startX} 0 L${startX} ${lineY - curveRadius} Q${startX} ${lineY} ${startX + curveRadius} ${lineY} L${
                cardCenterX - curveRadius
            } ${lineY} Q${cardCenterX} ${lineY} ${cardCenterX} ${verticalDrop} L${cardCenterX} 0`,
            disabled: item.disabled,
            delay: (leftPaths.length + 1 + i) * DELAY,
        };
    });

    const arrowDelay = leftPaths.length * DELAY;

    return (
        <svg width={totalWidth} height={lineY + 10} viewBox={`0 0 ${totalWidth} ${lineY + 8}`} fill="none">
            {leftPaths.map((path) => (
                <path
                    key={path.id}
                    d={path.d}
                    stroke="#d3dfe8"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    opacity={path.disabled ? 0.3 : 1}
                    strokeDasharray={PATH_LENGTH}
                    strokeDashoffset={PATH_LENGTH}
                >
                    <animate
                        attributeName="stroke-dashoffset"
                        from={PATH_LENGTH}
                        to="0"
                        dur={`${DURATION}s`}
                        begin={`${path.delay}s`}
                        fill="freeze"
                        calcMode="spline"
                        keySplines="0.25 0.1 0.25 1"
                    />
                </path>
            ))}
            {rightPaths.map((path) => (
                <path
                    key={path.id}
                    d={path.d}
                    stroke="#d3dfe8"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    opacity={path.disabled ? 0.3 : 1}
                    strokeDasharray={PATH_LENGTH}
                    strokeDashoffset={PATH_LENGTH}
                >
                    <animate
                        attributeName="stroke-dashoffset"
                        from={PATH_LENGTH}
                        to="0"
                        dur={`${DURATION}s`}
                        begin={`${path.delay}s`}
                        fill="freeze"
                        calcMode="spline"
                        keySplines="0.25 0.1 0.25 1"
                    />
                </path>
            ))}
            {leftPaths.length > 0 && (
                <polygon
                    points={`${hubCenterX - hubSpacing - 6},${arrowSize} ${hubCenterX - hubSpacing},0 ${hubCenterX - hubSpacing + 6},${arrowSize}`}
                    fill="#d3dfe8"
                    opacity="0"
                >
                    <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${arrowDelay}s`} fill="freeze" />
                </polygon>
            )}
            {rightPaths.length > 0 && (
                <polygon
                    points={`${hubCenterX + hubSpacing - 6},0 ${hubCenterX + hubSpacing},${arrowSize} ${hubCenterX + hubSpacing + 6},0`}
                    fill="#d3dfe8"
                    opacity="0"
                >
                    <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${arrowDelay}s`} fill="freeze" />
                </polygon>
            )}
        </svg>
    );
};
